import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Header, Footer } from "../components/Orchestrio";
import SEO from "../components/SEO";

const AboutPage = () => {
  const facts = [
    { label: "Founded", value: "2014" },
    { label: "Team size", value: "8 people" },
    { label: "Projects delivered", value: "150+" },
    { label: "Based in", value: "London, UK" }
  ];

  const principles = [
    {
      title: "We prioritise clarity over cleverness",
      description: "Simple, readable code. Clear documentation. Straightforward architecture. Your next developer should be able to understand what we built."
    },
    {
      title: "We build for maintainability",
      description: "Every system we create is designed to be updated, extended, and improved—not just delivered and forgotten."
    },
    {
      title: "We communicate directly",
      description: "No jargon. No sugarcoating. If something's going wrong, you'll hear about it immediately. If we think you're making a mistake, we'll tell you."
    },
    {
      title: "We take ownership",
      description: "We don't build things and walk away. We offer ongoing support because we believe in maintaining what we create."
    },
    {
      title: "We respect your time",
      description: "Meetings have agendas. Updates are concise. We do the work, not the performative busyness."
    },
    {
      title: "We're honest about what we can't do",
      description: "If a project isn't right for us, we'll say so. If you'd be better served by someone else, we'll refer you."
    }
  ];

  const team = [
    {
      name: "Alex Morgan",
      role: "Technical Director",
      description: "15 years building web systems. Previously engineering lead at a Series B SaaS company."
    },
    {
      name: "Sarah Chen",
      role: "Lead Developer",
      description: "Full-stack specialist. Obsessive about code quality and documentation."
    },
    {
      name: "James Wright",
      role: "Frontend Lead",
      description: "Design systems and accessibility. Makes things work properly on every device."
    },
    {
      name: "Emma Thompson",
      role: "Project Manager",
      description: "Keeps everything on track. Clear communication with clients at every stage."
    }
  ];

  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="about-page">
      <SEO 
        title="About"
        description="Orchestrio is a UK-based digital systems studio. We design and build websites and applications with clarity, reliability, and maintainability as the priorities."
      />
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#5F8FB8] text-sm font-medium tracking-wide mb-6">
            About
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            We're Orchestrio.
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            A UK-based digital systems studio that builds websites, web applications, 
            and integrations for businesses that have been burned by sloppy work before.
          </p>
        </div>
      </section>

      {/* Facts */}
      <section className="py-12 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap gap-x-12 gap-y-6">
            {facts.map((fact, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-[#F4F6F8]">{fact.value}</div>
                <div className="text-sm text-[#6B7280]">{fact.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The story */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold mb-6">The short version</h2>
            <div className="space-y-4 text-[#9CA3AF] leading-relaxed">
              <p>
                We started Orchestrio in 2014 after years of watching projects fail for preventable reasons: 
                unclear requirements, poor communication, technical debt, and agencies that disappeared after launch.
              </p>
              <p>
                We built this company on the opposite principles. Every project gets proper scoping. 
                Every system gets documentation. Every client gets honest communication.
              </p>
              <p>
                Ten years later, we've delivered over 150 projects and maintained relationships with 
                clients who've been with us since the beginning. Not because we're the flashiest 
                option—because we're reliable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">How we work</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <div key={index}>
                <h3 className="font-semibold mb-2">{principle.title}</h3>
                <p className="text-sm text-[#9CA3AF] leading-relaxed">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">The team</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {team.map((member, index) => (
              <div key={index} className="p-6 bg-[#1E1E1E] border border-[#2E2E2E]">
                <h3 className="font-semibold">{member.name}</h3>
                <p className="text-[#5F8FB8] text-sm mb-3">{member.role}</p>
                <p className="text-sm text-[#9CA3AF]">{member.description}</p>
              </div>
            ))}
          </div>

          <p className="text-sm text-[#6B7280] mt-6">
            Plus four more developers and designers who work with us on larger projects.
          </p>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-4">Based in the UK</h2>
              <p className="text-[#9CA3AF] max-w-md mb-6">
                We work with clients across the UK, building digital systems that solve real problems.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Get in touch <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
